import React, { useState, useEffect, useRef } from 'react';
import style from './ExerciseStat.module.css';

const CircleProgress = ({ percent, color, size = 80, strokeWidth = 6, animate }) => {
    const radius = (size - strokeWidth) / 2;
    const circumference = 2 * Math.PI * radius;
    const capped = Math.min(percent, 100);
    const targetOffset = circumference - (capped / 100) * circumference;

    return (
        <svg width={size} height={size} className={style.circle}>
            <circle
                cx={size / 2} cy={size / 2} r={radius}
                fill="none" stroke="#F0F0F0" strokeWidth={strokeWidth}
            />
            <circle
                cx={size / 2} cy={size / 2} r={radius}
                fill="none" stroke={color} strokeWidth={strokeWidth}
                strokeDasharray={circumference}
                strokeDashoffset={animate ? targetOffset : circumference}
                strokeLinecap="round"
                transform={`rotate(-90 ${size / 2} ${size / 2})`}
                style={{ transition: 'stroke-dashoffset 1s cubic-bezier(0.4, 0, 0.2, 1)' }}
            />
            <text x="50%" y="50%" dominantBaseline="central" textAnchor="middle"
                className={style.circleText} fill={color}>
                {animate ? percent : 0} <tspan className={style.circleUnit}>%</tspan>
            </text>
        </svg>
    );
};

const AnimatedNumber = ({ value, animate, suffix = '' }) => {
    const [display, setDisplay] = useState(0);
    const rafRef = useRef(null);

    useEffect(() => {
        if (!animate) { setDisplay(0); return; }

        const duration = 1000;
        const start = performance.now();

        const tick = (now) => {
            const elapsed = now - start;
            const progress = Math.min(elapsed / duration, 1);
            const eased = 1 - Math.pow(1 - progress, 3); // easeOutCubic
            setDisplay(Math.round(eased * value));
            if (progress < 1) rafRef.current = requestAnimationFrame(tick);
        };

        rafRef.current = requestAnimationFrame(tick);
        return () => { if (rafRef.current) cancelAnimationFrame(rafRef.current); };
    }, [animate, value]);

    return <>{display.toLocaleString()}{suffix}</>;
};

const ExerciseStat = ({ data }) => {
    const { duration, calorie } = data;
    const wrapRef = useRef(null);
    const [visible, setVisible] = useState(false);

    useEffect(() => {
        const el = wrapRef.current;
        if (!el) return;

        const observer = new IntersectionObserver(
            ([entry]) => { if (entry.isIntersecting) setVisible(true); },
            { threshold: 0.3 }
        );
        observer.observe(el);
        return () => observer.disconnect();
    }, []);

    const durationColor = duration.percent >= 100 ? 'var(--color-main-5, #3B82F6)' : 'var(--color-point, #E55151)';
    const calorieColor = calorie.percent >= 100 ? 'var(--color-main-5, #3B82F6)' : 'var(--color-point, #E55151)';

    return (
        <div className={style.statWrap} ref={wrapRef}>
            <div className={style.statItem}>
                <CircleProgress percent={duration.percent} color={durationColor} animate={visible} />
                <p className={style.label}>운동 시간</p>
                <p className={style.value}>
                    <strong><AnimatedNumber value={duration.current} animate={visible} /></strong>
                    <span> {duration.unit}</span>
                </p>
                <p className={style.goal}>{duration.goal}{duration.unit}</p>
            </div>

            <div className={style.statItem}>
                <CircleProgress percent={calorie.percent} color={calorieColor} animate={visible} />
                <p className={style.label}>소모 칼로리</p>
                <p className={style.value}>
                    <strong><AnimatedNumber value={calorie.current} animate={visible} /></strong>
                    <span> {calorie.unit}</span>
                </p>
                <p className={style.goal}>{calorie.goal.toLocaleString()}{calorie.unit}</p>
            </div>
        </div>
    );
};

export default ExerciseStat;